<template>
  <section class="relative">
    <JobDescription ref="jobDescription" :job="job" :accountID="accountID" />
    <Button id="modal-toggle" :class="popupClasses" variant="text" :aria-label="modalToggleLabel" @click="showPopup = true">
      <MoreIcon v-if="displayDots" />
    </Button>

    <Popup v-model="showPopup" popupHeader="Manage Job">
      <p class="text-body mb-16">{{ job.title }}</p>
      <JobActions :job="job" :accountType="accountType" :accountID="accountID" @close-job="closeJobConfirm" />
    </Popup>

    <JobCloseConfirmation :value="showCloseConfirmation" @input="showCloseConfirmation = $event" @confirm="closeJobMutation" />
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Button from '@/components/button/Button.vue';
import Popup from '@/components/popup/Popup.vue';
import MoreIcon from '@/graphics/icons/MoreIcon.vue';
import { CLOSE_JOB } from '@/graphql/account/mutations';
import { redirect } from '@/helpers/redirects';
import helperStatus from '@/helpers/status';

import JobDescription from './components/JobDescription.vue';
import JobActions from './components/JobActions.vue';
import JobCloseConfirmation from './components/JobCloseConfirmation.vue';

interface Job {
  id: string;
  title: string;
  status: string;
  isActive: boolean;
  parent: {
    id: string;
  };
  applications: {
    edges: Array<{
      node: {
        status: string;
        parentInterestStatus: string;
      };
    }>;
    totalCount: number;
  };
}

interface JobCardData {
  showPopup: boolean;
  showCloseConfirmation: boolean;
  helperStatus: typeof helperStatus;
}

export default defineComponent({
  name: 'SeekerJobCard',
  components: {
    Button,
    Popup,
    MoreIcon,
    JobDescription,
    JobActions,
    JobCloseConfirmation,
  },
  props: {
    job: {
      type: Object as () => Job,
      required: true,
      default: () => ({
        id: '',
        title: '',
        status: '',
        isActive: false,
        parent: {
          id: '',
        },
        applications: {
          edges: [],
          totalCount: 0,
        },
      }),
    },
    accountType: {
      type: String,
      required: true,
      default: '',
      validator: (value: string) =>
        ['family_concierge', 'seeker', 'basic', 'premium', 'corporate', 'parent', 'sitter', 'featured_sitter'].includes(value),
    },
    accountID: {
      type: String,
      required: true,
    },
  },
  data(): JobCardData {
    return {
      showPopup: false,
      showCloseConfirmation: false,
      helperStatus,
    };
  },
  computed: {
    // UI State
    modalToggleLabel(): string {
      return `Manage your job: ${this.job.title}`;
    },
    popupClasses(): string {
      const hasNewApplicants = this.job.applications.edges.some(({ node }) => node.parentInterestStatus === 'RECEIVED');
      const shouldShowBanner = this.job.status === 'PENDING' || hasNewApplicants;
      return `job-card-modal absolute top-8 right-8 ${shouldShowBanner ? '__with-banner' : ''}`;
    },
    displayDots(): boolean {
      return !(this.fcOwnsJob && this.totalOpenApplicants.length === 0);
    },

    // Job State
    fcOwnsJob(): boolean {
      return !(this.accountID === this.job.parent.id);
    },
    totalOpenApplicants(): Array<{ node: { status: string } }> {
      return this.job.applications.edges.filter((app) => app.node.status === 'open');
    },
    totalNewApplicants(): number {
      return this.job.applications.edges.filter(({ node }) => node.parentInterestStatus === 'RECEIVED').length;
    },
  },
  methods: {
    // UI State Management
    closeJobConfirm() {
      this.showPopup = false;
      this.showCloseConfirmation = true;
    },

    // Job Actions
    async closeJobMutation() {
      this.showCloseConfirmation = false;
      try {
        await this.$apollo.mutate({
          mutation: CLOSE_JOB,
          variables: { id: this.job.id },
        });
        redirect(`/jobs/action_with_uuid?job_action=job_closed_questionnaire&uuid=${this.job.id}`);
      } catch (error) {
        console.error('Failed to close job:', error);
      }
    },
  },
});
</script>
